import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  AiUsageDataService,
  AiUsageSummary,
  AiUsageGroupBy,
  AiUsageGroupRow,
  AiUsageEvent,
  AiModelPrice,
} from '../../services/aiUsageDataService';
import { toDateString } from '../../services/dateUtils';

// Green-led categorical palette (first colour matches the app accent).
const PALETTE = [
  '#26923f', '#3498db', '#f39c12', '#9b59b6', '#1abc9c',
  '#e67e22', '#e91e63', '#00bcd4', '#8bc34a', '#607d8b',
];
const AXIS = '#7e8a78';

interface RangePreset { key: string; label: string; days: number; }
const RANGE_PRESETS: RangePreset[] = [
  { key: '7', label: '7 days', days: 7 },
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
  { key: '365', label: '12 months', days: 365 },
];

const FEATURE_LABELS: Record<string, string> = {
  typo_correction: 'Typo correction',
  enrichment: 'Enrichment',
  embedding: 'Embedding',
};
const featureLabel = (f: string) => FEATURE_LABELS[f] ?? f;

// Long Bedrock model ids — show a compact tail for chart axes/legends.
const shortModel = (id: string) => {
  if (!id) return id;
  const cleaned = id.replace(/^eu\./, '').replace(/-v\d+:\d+$/, '');
  const parts = cleaned.split('.');
  return parts[parts.length - 1] || id;
};

const startForPreset = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  return toDateString(d);
};

const GROUP_OPTIONS: { key: AiUsageGroupBy; label: string }[] = [
  { key: 'feature', label: 'Type' },
  { key: 'model', label: 'Model' },
  { key: 'user', label: 'User' },
  { key: 'child', label: 'Child' },
  { key: 'post', label: 'Post' },
];

interface AiUsagePanelProps {
  /** The organisation whose usage is shown — chosen by the system admin. */
  orgId: number;
}

const AiUsagePanel: React.FC<AiUsagePanelProps> = ({ orgId }) => {
  const [rangeKey, setRangeKey] = useState<string>('30');
  const [from, setFrom] = useState<string>(startForPreset(30));
  const [to, setTo] = useState<string>(toDateString(new Date()));

  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [groupBy, setGroupBy] = useState<AiUsageGroupBy>('feature');
  const [groups, setGroups] = useState<AiUsageGroupRow[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const [events, setEvents] = useState<AiUsageEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [featureFilter, setFeatureFilter] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const PAGE = 50;

  const [prices, setPrices] = useState<AiModelPrice[]>([]);

  const currency = summary?.currency || 'USD';

  const fmtCost = useMemo(() => {
    return (v: number | null | undefined): string => {
      if (v == null) return '—';
      const decimals = v !== 0 && Math.abs(v) < 1 ? 4 : 2;
      const sym = currency === 'USD' ? '$' : '';
      const body = v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      return sym ? `${sym}${body}` : `${body} ${currency}`;
    };
  }, [currency]);

  const fmtInt = (v: number) => v.toLocaleString();

  // Apply a preset range.
  const applyPreset = (preset: RangePreset) => {
    setRangeKey(preset.key);
    setFrom(startForPreset(preset.days));
    setTo(toDateString(new Date()));
    setOffset(0);
  };

  // Summary load.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    AiUsageDataService.fetchSummary(orgId, from, to)
      .then(data => { if (!cancelled) setSummary(data); })
      .catch(err => {
        console.error('Failed to load AI usage summary', err);
        if (!cancelled) setError('Could not load AI usage. Please try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, from, to]);

  // Grouped breakdown load.
  useEffect(() => {
    let cancelled = false;
    setGroupsLoading(true);
    AiUsageDataService.fetchGrouped(orgId, groupBy, from, to)
      .then(data => { if (!cancelled) setGroups(data.groups); })
      .catch(err => { console.error('Failed to load AI usage breakdown', err); if (!cancelled) setGroups([]); })
      .finally(() => { if (!cancelled) setGroupsLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, groupBy, from, to]);

  // Granular events load.
  useEffect(() => {
    let cancelled = false;
    setEventsLoading(true);
    AiUsageDataService.fetchEvents(orgId, from, to, {
      feature: featureFilter || undefined,
      limit: PAGE,
      offset,
    })
      .then(data => { if (!cancelled) setEvents(data.events); })
      .catch(err => { console.error('Failed to load AI usage events', err); if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setEventsLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, from, to, featureFilter, offset]);

  // Prices (once — the book is global, but the route lives under the org).
  useEffect(() => {
    let cancelled = false;
    AiUsageDataService.fetchPrices(orgId)
      .then(data => { if (!cancelled) setPrices(data); })
      .catch(err => console.error('Failed to load model prices', err));
    return () => { cancelled = true; };
  }, [orgId]);

  const featureChartData = useMemo(
    () => (summary?.byFeature || []).map(r => ({ name: featureLabel(r.feature), cost: r.cost, calls: r.calls })),
    [summary],
  );
  const modelChartData = useMemo(
    () => (summary?.byModel || []).map(r => ({ name: shortModel(r.modelId), cost: r.cost, calls: r.calls })),
    [summary],
  );
  const userChartData = useMemo(
    () => (summary?.byUser || []).slice(0, 8).map(r => ({ name: r.userName, cost: r.cost })),
    [summary],
  );
  const childChartData = useMemo(
    () => (summary?.byChild || []).slice(0, 8).map(r => ({ name: r.childName, cost: r.cost })),
    [summary],
  );
  const dailyChartData = useMemo(
    () => (summary?.daily || []).map(r => ({ date: r.date.slice(5), cost: r.cost, calls: r.calls })),
    [summary],
  );

  const costTooltip = (value: any) => fmtCost(typeof value === 'number' ? value : Number(value));

  const totalTokens = summary ? summary.totals.inputTokens + summary.totals.outputTokens : 0;

  return (
    <div className="p-4" style={{ flex: 1, overflowY: 'auto' }}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h3 className="font-primary m-0">AI Usage</h3>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="btn-group" role="group" aria-label="Date range">
            {RANGE_PRESETS.map(preset => (
              <button
                key={preset.key}
                type="button"
                className={`btn btn-sm ${rangeKey === preset.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 150 }}
            value={from}
            max={to}
            onChange={e => { setRangeKey('custom'); setFrom(e.target.value); setOffset(0); }}
            aria-label="From date"
          />
          <span className="text-muted">to</span>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 150 }}
            value={to}
            min={from}
            max={toDateString(new Date())}
            onChange={e => { setRangeKey('custom'); setTo(e.target.value); setOffset(0); }}
            aria-label="To date"
          />
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Summary cards */}
      <div className="row g-3 mb-4">
        <SummaryCard title="Total cost" value={loading ? '…' : fmtCost(summary?.totals.cost ?? 0)} icon="fa-coins" />
        <SummaryCard title="AI calls" value={loading ? '…' : fmtInt(summary?.totals.calls ?? 0)} icon="fa-bolt" />
        <SummaryCard title="Tokens" value={loading ? '…' : fmtInt(totalTokens)} icon="fa-hashtag"
          sub={summary ? `${fmtInt(summary.totals.inputTokens)} in / ${fmtInt(summary.totals.outputTokens)} out` : undefined} />
        <SummaryCard title="Failed / unpriced" value={loading ? '…' : `${fmtInt(summary?.totals.failedCalls ?? 0)} / ${fmtInt(summary?.totals.unpricedCalls ?? 0)}`}
          icon="fa-triangle-exclamation"
          warn={!!summary && (summary.totals.failedCalls > 0 || summary.totals.unpricedCalls > 0)} />
      </div>

      {summary && summary.totals.unpricedCalls > 0 && (
        <div className="alert alert-warning py-2" style={{ fontSize: '0.9em' }}>
          {fmtInt(summary.totals.unpricedCalls)} call(s) used a model with no price in the book, so their cost is not
          included in the totals. See the model pricing table below.
        </div>
      )}

      {/* Charts */}
      <div className="row g-3 mb-4">
        <ChartCard title="Cost over time" colClass="col-12">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e9e4" />
              <XAxis dataKey="date" stroke={AXIS} fontSize={12} />
              <YAxis stroke={AXIS} fontSize={12} />
              <Tooltip formatter={costTooltip} />
              <Line type="monotone" dataKey="cost" name="Cost" stroke={PALETTE[0]} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cost by type" colClass="col-12 col-lg-6">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={featureChartData} dataKey="cost" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {featureChartData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip formatter={costTooltip} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cost by model" colClass="col-12 col-lg-6">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={modelChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e9e4" />
              <XAxis dataKey="name" stroke={AXIS} fontSize={11} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis stroke={AXIS} fontSize={12} />
              <Tooltip formatter={costTooltip} />
              <Bar dataKey="cost" name="Cost" fill={PALETTE[1]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top cost by user" colClass="col-12 col-lg-6">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={userChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e9e4" />
              <XAxis type="number" stroke={AXIS} fontSize={12} />
              <YAxis type="category" dataKey="name" stroke={AXIS} fontSize={11} width={120} />
              <Tooltip formatter={costTooltip} />
              <Bar dataKey="cost" name="Cost" fill={PALETTE[3]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top cost by child" colClass="col-12 col-lg-6">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={childChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e6e9e4" />
              <XAxis type="number" stroke={AXIS} fontSize={12} />
              <YAxis type="category" dataKey="name" stroke={AXIS} fontSize={11} width={120} />
              <Tooltip formatter={costTooltip} />
              <Bar dataKey="cost" name="Cost" fill={PALETTE[4]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Group-by breakdown table */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <h5 className="m-0">Breakdown</h5>
            <div className="btn-group btn-group-sm" role="group" aria-label="Group by">
              {GROUP_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  className={`btn ${groupBy === opt.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setGroupBy(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>{GROUP_OPTIONS.find(g => g.key === groupBy)?.label}</th>
                  <th className="text-end">Calls</th>
                  <th className="text-end">Input tokens</th>
                  <th className="text-end">Output tokens</th>
                  <th className="text-end">Cost</th>
                </tr>
              </thead>
              <tbody>
                {groupsLoading ? (
                  <tr><td colSpan={5} className="text-center text-muted py-3">Loading…</td></tr>
                ) : groups.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted py-3">No usage in this period.</td></tr>
                ) : groups.map(g => (
                  <tr key={g.key ?? g.label}>
                    <td>{groupBy === 'feature' ? featureLabel(g.label) : g.label}
                      {g.unpricedCalls > 0 && <span className="badge bg-warning text-dark ms-2">{g.unpricedCalls} unpriced</span>}
                    </td>
                    <td className="text-end">{fmtInt(g.calls)}</td>
                    <td className="text-end">{fmtInt(g.inputTokens)}</td>
                    <td className="text-end">{fmtInt(g.outputTokens)}</td>
                    <td className="text-end">{fmtCost(g.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Granular drill-down */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <h5 className="m-0">Calls</h5>
            <div className="d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                style={{ width: 180 }}
                value={featureFilter}
                onChange={e => { setFeatureFilter(e.target.value); setOffset(0); }}
                aria-label="Filter by type"
              >
                <option value="">All types</option>
                <option value="typo_correction">Typo correction</option>
                <option value="enrichment">Enrichment</option>
                <option value="embedding">Embedding</option>
              </select>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Model</th>
                  <th>User</th>
                  <th>Child</th>
                  <th className="text-end">In</th>
                  <th className="text-end">Out</th>
                  <th className="text-end">Cost</th>
                  <th className="text-center">OK</th>
                </tr>
              </thead>
              <tbody>
                {eventsLoading ? (
                  <tr><td colSpan={9} className="text-center text-muted py-3">Loading…</td></tr>
                ) : events.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-muted py-3">No calls in this period.</td></tr>
                ) : events.map(ev => (
                  <tr key={ev.id}>
                    <td title={ev.occurredAt}>{new Date(ev.occurredAt).toLocaleString()}</td>
                    <td>{featureLabel(ev.feature)}</td>
                    <td title={ev.modelId}>{shortModel(ev.modelId)}</td>
                    <td>{ev.userName || (ev.userId ? `User #${ev.userId}` : '—')}</td>
                    <td>{ev.childName || (ev.postId ? '—' : 'Unattributed')}</td>
                    <td className="text-end">{fmtInt(ev.inputTokens)}</td>
                    <td className="text-end">{fmtInt(ev.outputTokens)}</td>
                    <td className="text-end">{ev.priced ? fmtCost(ev.cost) : <span className="text-muted" title="No price for this model/date">unpriced</span>}</td>
                    <td className="text-center">
                      {ev.succeeded
                        ? <i className="fas fa-check text-success" aria-label="Succeeded" />
                        : <i className="fas fa-times text-danger" aria-label="Failed" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Showing {events.length ? offset + 1 : 0}–{offset + events.length}
            </small>
            <div className="btn-group btn-group-sm">
              <button type="button" className="btn btn-outline-secondary" disabled={offset === 0 || eventsLoading}
                onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
              <button type="button" className="btn btn-outline-secondary" disabled={events.length < PAGE || eventsLoading}
                onClick={() => setOffset(offset + PAGE)}>Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* Read-only model pricing */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="mb-1">Model pricing</h5>
          <p className="text-muted" style={{ fontSize: '0.9em' }}>
            Rates behind the cost figures (USD per 1,000 tokens). Managed centrally; read-only here. Each call is
            costed using the rate effective on its date.
          </p>
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Model</th>
                  <th className="text-end">Input / 1k</th>
                  <th className="text-end">Output / 1k</th>
                  <th>Currency</th>
                  <th>Effective from</th>
                </tr>
              </thead>
              <tbody>
                {prices.length === 0 ? (
                  <tr><td colSpan={5} className="text-center text-muted py-3">No prices configured.</td></tr>
                ) : prices.map(pr => (
                  <tr key={`${pr.modelId}-${pr.effectiveFrom}`}>
                    <td title={pr.modelId}>{pr.modelId}</td>
                    <td className="text-end">{pr.inputPer1k.toFixed(6)}</td>
                    <td className="text-end">{pr.outputPer1k.toFixed(6)}</td>
                    <td>{pr.currency}</td>
                    <td>{pr.effectiveFrom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard: React.FC<{ title: string; value: string; icon: string; sub?: string; warn?: boolean }> =
  ({ title, value, icon, sub, warn }) => (
    <div className="col-6 col-lg-3">
      <div className={`card h-100 ${warn ? 'border-warning' : ''}`}>
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <span className="text-muted" style={{ fontSize: '0.85em' }}>{title}</span>
            <i className={`fas ${icon} ${warn ? 'text-warning' : 'text-secondary'}`} aria-hidden="true" />
          </div>
          <div className="fs-4 fw-semibold mt-1">{value}</div>
          {sub && <div className="text-muted" style={{ fontSize: '0.8em' }}>{sub}</div>}
        </div>
      </div>
    </div>
  );

const ChartCard: React.FC<{ title: string; colClass: string; children: React.ReactNode }> =
  ({ title, colClass, children }) => (
    <div className={colClass}>
      <div className="card h-100">
        <div className="card-body">
          <h6 className="text-muted mb-3">{title}</h6>
          {children}
        </div>
      </div>
    </div>
  );

export default AiUsagePanel;
